import { useMemo } from 'react'
import { KpiStrip, MetricCard, Panel } from '@/components/erp/primitives'
import { EmptyState, Note } from '@/shared/ui/states'
import { fmtNumber, fmtPercent, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { EquityIndexRow, EquityResponse } from '@/shared/api/types'
import { analyticsEquity } from '../api'
import { Excluded, NotRun, Pct, Unavailable } from '../parts'
import { AnalyticsScreen } from './shell'

/**
 * Who the portfolio reached, against who was eligible to be reached.
 *
 * ## The index needs both sides, and that is why it is often absent
 *
 * A representation index is the enrolled share of a group divided by its
 * eligible share. Without the eligible side it is not a weaker version of the
 * same figure — it is a different figure entirely. "38% of our beneficiaries are
 * women" says nothing about fairness until you know what share of the eligible
 * pool is women; if the answer is 38%, the programme is exactly proportional.
 *
 * The backend only has an eligible-side breakdown where an eligibility snapshot
 * carries one. Where it does not, `unavailableReason` is set and NO index is
 * shown — a wrong fairness number is worse than none, because it is the kind of
 * number that ends up in a complaint.
 *
 * ## Direction, not hue
 *
 * Bars run left or right from the proportional line. Under- and
 * over-representation are opposite directions from a fixed origin, which every
 * reader can decode; two colours for the two directions would put the polarity
 * in a channel that not every reader has.
 */
export function ProgramEquityScreen() {
  return (
    <AnalyticsScreen
      title="Equity & reach"
      description="Enrolled share against eligible share, by dimension."
      load={(f) => analyticsEquity(f)}
      isEmpty={(d) => (d.rows?.length ?? 0) === 0 && !d.unavailableReason}
      exportKind="EQUITY"
    >
      {(d) => <EquityBody d={d} />}
    </AnalyticsScreen>
  )
}

function EquityBody({ d }: { d: EquityResponse }) {
  const grouped = useMemo(() => {
    const m = new Map<string, EquityIndexRow[]>()
    ;(d.rows ?? []).forEach((r) => {
      const list = m.get(r.dimension) ?? []
      list.push(r)
      m.set(r.dimension, list)
    })
    return [...m.entries()].map(([dimension, rows]) => ({
      dimension,
      rows: [...rows].sort((a, b) => b.enrolled - a.enrolled),
    }))
  }, [d.rows])

  if (d.unavailableReason) {
    return (
      <div className="space-y-4">
        <NotRun what="The representation index" reason={d.unavailableReason} />
        <Note tone="info">
          The index compares the share of beneficiaries in a group with that group's share
          of the eligible pool. Both sides are needed. Without the eligible side, the only
          honest statement is the beneficiary breakdown on its own — which says who was
          reached and nothing at all about whether that was fair.
        </Note>
        <Excluded items={[{ count: d.excludedUnlinked, label: 'not linked to a voter record' }]} />
      </div>
    )
  }

  const worst = [...(d.rows ?? [])]
    .filter((r) => r.representationIndex != null)
    .sort((a, b) => Number(a.representationIndex) - Number(b.representationIndex))[0]

  const withIndex = (d.rows ?? []).filter((r) => r.representationIndex != null)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Dimensions" value={fmtNumber(grouped.length)}
          sub={`${fmtNumber(d.rows?.length ?? 0)} groups in total`} />
        <MetricCard label="With an index" value={fmtNumber(withIndex.length)}
          sub={`${fmtNumber((d.rows?.length ?? 0) - withIndex.length)} have no eligible-side share`} />
        <MetricCard
          label="Most under-represented"
          value={worst ? equityValueLabel(worst.value) : <Unavailable />}
          accent
          // The index alone lets a group of three people win this card. The
          // headcount is what tells the reader whether the ratio is a finding.
          sub={worst
            ? `${Number(worst.representationIndex).toFixed(2)}× their share of the eligible pool · ${fmtNumber(worst.enrolled)} enrolled`
            : undefined}
        />
        <MetricCard label="Excluded" value={fmtNumber(d.excludedUnlinked)}
          sub="Not linked to a voter record, so no dimensions are known" />
      </KpiStrip>

      <Note tone="info">
        An index of 1.00 means a group was enrolled in exactly the proportion it makes up of
        the eligible pool. Below 1.00 is under-represented, above is over-represented.
        Neither is automatically wrong — a programme deliberately aimed at one group is
        supposed to over-represent it, and the index is how you check the aim landed.
      </Note>

      {grouped.length === 0 && (
        <EmptyState title="Nothing to compare"
          hint="No group in scope has both an enrolled and an eligible share." />
      )}

      {grouped.map((g) => (
        <Panel key={g.dimension} title={humanise(g.dimension)}
          description="Enrolled share against eligible share. The centre line is proportional representation.">
          <ul className="space-y-2">
            {g.rows.map((r) => (
              <EquityBar key={`${r.dimension}-${r.value}`} row={r} />
            ))}
          </ul>
          {/* The scale, once per panel. Without it a bar's length is a feeling. */}
          <div className="mt-2 grid grid-cols-[minmax(0,9rem)_1fr_4rem_10rem] gap-3">
            <span />
            <span className="flex justify-between text-erp-xs text-muted-foreground/70">
              <span>0.00×</span><span>1.00×</span><span>2.00× or more</span>
            </span>
            <span /><span />
          </div>
        </Panel>
      ))}

      <Excluded items={[{ count: d.excludedUnlinked, label: 'not linked to a voter record' }]} />
    </div>
  )
}

/**
 * One group's bar. The origin is 1.00 and the bar runs left when the group is
 * under-represented and right when it is over. The index is capped visually at
 * 2× so one enormous over-representation does not flatten every other row to
 * nothing — the number beside the bar is never capped.
 */
function EquityBar({ row }: { row: EquityIndexRow }) {
  const idx = row.representationIndex == null ? null : Number(row.representationIndex)
  const span = idx == null ? 0 : Math.min(Math.abs(idx - 1), 1) * 50

  return (
    <li className="grid grid-cols-[minmax(0,9rem)_1fr_4rem_10rem] items-center gap-3">
      <span className="truncate text-erp-xs" title={row.value}>{equityValueLabel(row.value)}</span>
      <span className="relative block h-4">
        <span className="absolute inset-y-0 left-1/2 w-px bg-muted-foreground/40" aria-hidden />
        {idx != null && (
          <span
            className={cn('absolute top-0.5 h-3 rounded-sm')}
            style={{
              background: 'var(--chart-1)',
              left: idx < 1 ? `${50 - span}%` : '50%',
              width: `${Math.max(span, 0.6)}%`,
            }}
            title={`${fmtNumber(row.enrolled)} enrolled${row.eligible != null ? ` of ${fmtNumber(row.eligible)} eligible` : ''}`}
          />
        )}
      </span>
      <span className="text-erp-sm tabular-nums text-right font-medium">
        {idx == null ? <Unavailable short /> : `${idx.toFixed(2)}×`}
      </span>
      <span className="text-erp-xs tabular-nums text-right text-muted-foreground whitespace-nowrap">
        {idx == null
          ? 'no eligible-side share'
          : (
            <>
              <Pct value={row.enrolledShare} digits={0} shortReason /> enrolled
              {row.eligibleShare != null && <> · {fmtPercent(Number(row.eligibleShare), 0)} eligible</>}
            </>
          )}
      </span>
    </li>
  )
}

/**
 * Dimension VALUES arrive as codes from the eligibility snapshot, and the ones
 * the system currently produces are age bands (`A18_24`, `A65_PLUS`). The `A`
 * prefix exists so the code is not a leading digit; printing it back at the
 * reader as "A18 24" is the backend's storage format leaking into a report.
 *
 * Anything that does not match falls through to the generic humaniser — the set
 * of dimensions is not fixed, and a value this does not recognise must still
 * render as words rather than as nothing.
 */
function equityValueLabel(value: string): string {
  const band = /^A(\d{2})_(\d{2})$/.exec(value)
  if (band) return `${band[1]}–${band[2]}`
  const plus = /^A(\d{2})_PLUS$/.exec(value)
  if (plus) return `${plus[1]}+`
  return humanise(value)
}
